import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authExtrasAPI, formatApiError } from '../services/api';
import PasscodeInput from './PasscodeInput';
import { Button } from './ui/button';
import { Lock, SignOut } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SESSION_KEY = 'divispend_passcode_verified';

/**
 * Wraps the entire authenticated app and blocks it behind a passcode prompt
 * if the user has one set. Verification is remembered per-tab via sessionStorage,
 * so the user only has to enter the passcode once per browser session.
 *
 * If the user doesn't have a passcode set, this is a no-op passthrough.
 */
export default function PasscodeGate({ children }) {
  const { user, isAuthenticated, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [needsPasscode, setNeedsPasscode] = useState(false);
  const [length, setLength] = useState(6);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(async () => {
    if (!isAuthenticated) { setNeedsPasscode(false); setLoading(false); return; }
    // Session-level trust — entered once per tab
    if (sessionStorage.getItem(SESSION_KEY) === '1') {
      setNeedsPasscode(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await authExtrasAPI.passcodeStatus();
      setNeedsPasscode(Boolean(data.passcode_set));
      if (data.passcode_length) setLength(data.passcode_length);
    } catch (err) {
      console.error('Passcode status check failed:', err);
      setNeedsPasscode(false);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { check(); }, [check]);

  // Clear the session flag when user logs out
  useEffect(() => {
    if (!isAuthenticated) sessionStorage.removeItem(SESSION_KEY);
  }, [isAuthenticated]);

  const handleVerify = async (entered) => {
    if (entered.length !== length) return;
    setSubmitting(true);
    try {
      await authExtrasAPI.verifyPasscode(entered);
      sessionStorage.setItem(SESSION_KEY, '1');
      setNeedsPasscode(false);
      setCode('');
    } catch (err) {
      toast.error(formatApiError(err));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !needsPasscode) return children;

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4 py-12" data-testid="passcode-lock-screen">
      <div className="w-full max-w-md">
        <div className="bg-[#0d150e] border border-[#22C55E]/20 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[#22C55E]/15 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-8 h-8 text-[#22C55E]" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</h1>
          <p className="text-[#999999] text-sm mb-8">Enter your passcode to unlock Divispend.</p>

          <PasscodeInput
            length={length}
            value={code}
            onChange={(c) => { setCode(c); if (c.length === length) handleVerify(c); }}
            disabled={submitting}
            autoFocus
          />

          {submitting && <p className="text-xs text-[#999999] mt-4">Verifying…</p>}

          <div className="mt-8 pt-6 border-t border-[#22C55E]/10">
            <Button
              variant="ghost"
              onClick={logout}
              className="text-[#999999] hover:text-white hover:bg-[#222222] rounded-sm text-sm"
              data-testid="passcode-logout-btn"
            >
              <SignOut className="w-4 h-4 mr-2" /> Sign out instead
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
